import { useEffect, useRef } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import * as elementsApi from './api'
import { invalidateContractFinancing } from '@/features/contract/financing/hooks'
import type { ContractElement } from './types'

export const contractElementKeys = {
  byContract: (contractId: number) => ['contract-elements', contractId] as const,
}

export function useContractElements(contractId: number) {
  return useQuery({
    queryKey: contractElementKeys.byContract(contractId),
    queryFn: () => elementsApi.listContractElements(contractId),
    enabled: !!contractId,
  })
}

export function useCompleteElementStage(contractId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (elementId: number) => elementsApi.completeElementStage(contractId, elementId),
    onSuccess: (data: ContractElement[]) => {
      qc.setQueryData(contractElementKeys.byContract(contractId), data)
      qc.invalidateQueries({ queryKey: ['contract-elements', contractId, 'gate'] })
    },
  })
}

export function useOverrideElementStage(contractId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { elementId: number; targetStageCode: string }) =>
      elementsApi.overrideElementStage(contractId, vars.elementId, vars.targetStageCode),
    onSuccess: (data: ContractElement[]) => {
      qc.setQueryData(contractElementKeys.byContract(contractId), data)
      qc.invalidateQueries({ queryKey: ['contract-elements', contractId, 'gate'] })
    },
  })
}

export const productFormSchemaKey = (productCode: string) =>
  ['admin', 'forms', 'schema', `product/${productCode}`] as const

export function useElementGate(contractId: number, elementId: number) {
  return useQuery({
    queryKey: ['contract-elements', contractId, 'gate', elementId] as const,
    queryFn: () => elementsApi.getElementGate(contractId, elementId),
    enabled: !!contractId && !!elementId,
  })
}

export function useProductFormSchema(productCode: string) {
  return useQuery({
    queryKey: productFormSchemaKey(productCode),
    queryFn: () => elementsApi.getProductFormSchema(productCode),
    enabled: !!productCode,
    retry: false,
  })
}

export function useUpdateElementSection(contractId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { elementId: number; sectionCode: string; extras: Record<string, unknown> }) =>
      elementsApi.updateElementSection(contractId, vars.elementId, vars.sectionCode, vars.extras),
    onSuccess: (data: ContractElement[]) => {
      qc.setQueryData(contractElementKeys.byContract(contractId), data)
      // a filled field may open a gate -> refresh element gate status
      qc.invalidateQueries({ queryKey: ['contract-elements', contractId, 'gate'] })
      // cena produktu (ELEMENT_PRICE) wplywa na finansowanie -> odswiez te zapytania
      invalidateContractFinancing(qc, contractId)
    },
  })
}

/** Debounced auto-save of element field values, mirroring the contract section updater. */
export function useDebouncedElementSectionUpdate(
  contractId: number,
  elementId: number,
  sectionCode: string,
  delayMs = 1500,
) {
  const mutation = useUpdateElementSection(contractId)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const buffer = useRef<Record<string, unknown>>({})

  useEffect(() => {
    return () => {
      if (timer.current) clearTimeout(timer.current)
    }
  }, [])

  const queue = (changes: Record<string, unknown>) => {
    buffer.current = { ...buffer.current, ...changes }
    if (timer.current) clearTimeout(timer.current)
    timer.current = setTimeout(() => {
      const extras = buffer.current
      buffer.current = {}
      mutation.mutate({ elementId, sectionCode, extras })
    }, delayMs)
  }

  return { queue, status: mutation.status, error: mutation.error }
}
